import { Component } from '@verndale/core';
import Swiper from 'swiper';
import { Navigation, Pagination, A11y, Keyboard } from 'swiper/modules';
import '../../scss/modules/full-height-carousel.scss';
import 'swiper/css';
import { BREAKPOINTS } from '../helpers/helpers';
import { eventBus, initialBreakpoint } from '../helpers/resize';

class ScrollImageGrid extends Component {
  private swiper: Swiper | null = null;
  private isMobile = false;

  constructor(el: HTMLElement) {
    super(el);

    this.handleBreakpointChange(initialBreakpoint());
  }

  setupDefaults() {
    this.dom = {
      swiper: this.el.querySelector<HTMLDivElement>('.swiper'),
      slides: this.el.querySelectorAll<HTMLDivElement>('.swiper-slide'),
      pagination: this.el.querySelector<HTMLDivElement>('.swiper-pagination'),
      next: this.el.querySelector<HTMLDivElement>('.swiper-button-next'),
      prev: this.el.querySelector<HTMLDivElement>('.swiper-button-prev'),
      stickyContainer: this.el.querySelector<HTMLElement>('.scroll-image-grid__static-card'),
      stickyCard: this.el.querySelector<HTMLElement>('.scroll-image-grid__static-card .card')
    };
  }

  addListeners(): void {
    eventBus.subscribe('breakpointChange', this.handleBreakpointChange.bind(this));
    window.addEventListener('scroll', this.handleScroll.bind(this));
  }

  handleScroll() {
    const tabletLandscapeBreakpoint = 1024; // Define the breakpoint for tablet landscape

    // If the window's width is less than or equal to the breakpoint, return immediately
    if (window.innerWidth <= tabletLandscapeBreakpoint) {
      this.stickyCardCleanup();
      return;
    }

    const stickyCard = this.dom.stickyCard as HTMLElement;
    const parentContainer = this.dom.stickyContainer as HTMLElement;

    stickyCard.style.width = `${parentContainer.offsetWidth}px`;

    // Calculate the initial top position of the sticky card relative to the document
    let offsetParent = stickyCard.offsetParent as HTMLElement;
    while (offsetParent != null) {
      offsetParent = offsetParent.offsetParent as HTMLElement;
    }

    // Calculate the top and bottom boundaries of the parent container relative to the document
    let parentContainerTop = parentContainer.offsetTop;
    let parentContainerBottom = parentContainerTop + parentContainer.offsetHeight;
    offsetParent = parentContainer.offsetParent as HTMLElement;
    while (offsetParent != null) {
      parentContainerTop += offsetParent.offsetTop;
      parentContainerBottom += offsetParent.offsetTop;
      offsetParent = offsetParent.offsetParent as HTMLElement;
    }

    const scrollY = window.scrollY;
    const bottomParent = parentContainerBottom - stickyCard.offsetHeight;

    if (scrollY > parentContainerTop && scrollY < bottomParent) {
      stickyCard.style.position = 'fixed';
      stickyCard.style.top = '100px'; // Stick to the top of the viewport
    } else if (scrollY >= bottomParent) {
      stickyCard.style.position = 'absolute';
      stickyCard.style.top = 'auto'; // Reset to the initial position
      stickyCard.style.bottom = '0'; // Stick to the bottom of the parent container
    } else if (scrollY < parentContainerTop) {
      stickyCard.style.position = 'relative';
      stickyCard.style.top = 'auto'; // Reset to the initial position
    }
  }

  initSwiper() {
    this.stickyCardCleanup();
    if (
      (this.isMobile && (this.dom.slides as NodeList).length < 2) ||
      (!this.isMobile && (this.dom.slides as NodeList).length < 3)
    ) {
      this.el.classList.add('scroll-image-grid--single-slide');
      return;
    } else {
      this.el.classList.remove('scroll-image-grid--single-slide');
    }

    if (!this.swiper) {
      this.swiper = new Swiper(this.dom.swiper as HTMLElement, {
        modules: [Navigation, Pagination, A11y, Keyboard],
        slidesPerView: 1,
        spaceBetween: 16,
        loop: true,
        breakpoints: {
          [BREAKPOINTS.tablet]: {
            slidesPerView: 2,
            spaceBetween: 16
          },
          [BREAKPOINTS.tabletLandscape]: {
            slidesPerView: 2,
            spaceBetween: 16
          }
        },
        pagination: {
          el: this.dom.pagination as HTMLElement,
          type: 'fraction',
          renderFraction: (currentClass: string, totalClass: string) => {
            return `<span class="swiper-pagination-current">${currentClass}</span>/<span class="swiper-pagination-total">${totalClass}</span>`;
          }
        },
        keyboard: {
          enabled: true,
          onlyInViewport: true
        },
        navigation: {
          nextEl: this.dom.next as HTMLElement,
          prevEl: this.dom.prev as HTMLElement
        }
      });
    }
  }

  stickyCardCleanup() {
    const stickyCard = this.dom.stickyCard as HTMLElement;
    stickyCard.style.position = 'relative';
    stickyCard.style.top = 'auto';
    stickyCard.style.bottom = 'auto';
    stickyCard.style.width = '100%';
  }

  handleBreakpointChange(breakpoint: string) {
    if (breakpoint === 'mobile' || breakpoint === 'tablet') {
      this.isMobile = breakpoint === 'mobile';
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.initSwiper();
    } else {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }
}

export default ScrollImageGrid;
